import { Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  ITransactionFormData,
  ITransactionFormDataDocRelations,
  ITxFormClientData,
} from '@app/transaction-builder/model/transaction-form-data.model';
import { DocumentRelationValidator } from '@app/transaction-builder/validators/document-relations.validator';
import { ClientDocumentRelation, SignatureType, SigningRequestChannel } from 'ngx-mat-esign360';
import { v4 as uuidv4 } from 'uuid';
import { RequestChannelContactValidator } from '@app/transaction-builder/validators/request-channel-contact.validator';
import { SignFieldsContactValidator } from '@app/transaction-builder/validators/sign-fields-contact.validator';
import { SignFieldsByClientCidGetter } from '../model/sign-fields-by-client-cid-getter.model';
import { NumbersOnlyValidator } from '@app/shared/validators/numbers-only.validator';
import { ProfileService } from '@app/core/services/profile.service';
import { isClientFieldVisible } from '../utils/form.util';
import { IClientFieldsVisibility } from '@app/core/model/env-config.model';
import { VisibleClientFieldsValidator } from '@app/transaction-builder/validators/visible-client-field.validator';

@Injectable({
  providedIn: 'root',
})
export class TransactionFormService {
  constructor(private formBuilder: FormBuilder, private profileService: ProfileService) {}

  createFormGroup(
    getSignFieldsByClientCid: SignFieldsByClientCidGetter,
    formData?: ITransactionFormData
  ): FormGroup {
    const profile = this.profileService.getCurrentProfile();

    const form = this.formBuilder.group({
      name: ['', [Validators.required]],
      clients: this.createClientsFormArray(getSignFieldsByClientCid, formData?.clients),
      invokingUser: this.formBuilder.group({
        sendMailIfCompleted: [
          formData?.invokingUser?.sendMailIfCompleted ?? profile.defaultSendMailIfCompleted,
        ],
        email: ['', [Validators.email]],
      }),
      documents: this.formBuilder.group({
        clientsToDocs: this.createClientsToDocsFormArray(formData?.documents.clientsToDocs),
      }),
    });
    return form;
  }

  createClientsFormArray(
    getSignFieldsByClientCid: SignFieldsByClientCidGetter,
    clients: ITxFormClientData[]
  ): FormArray {
    if (Array.isArray(clients)) {
      return this.formBuilder.array(
        clients.map((client) => this.createClientFormGroup(getSignFieldsByClientCid))
      );
    }
    return this.formBuilder.array([this.createClientFormGroup(getSignFieldsByClientCid)]);
  }

  composeClientFieldValidators(
    fieldName: keyof IClientFieldsVisibility,
    additionalValidators: ValidatorFn[] = []
  ): ValidatorFn[] {
    const clientFieldsVisibility = this.profileService.getCurrentProfile().clientFieldsVisibility;
    const baseValidators = isClientFieldVisible(clientFieldsVisibility, fieldName)
      ? [Validators.required]
      : [];

    return baseValidators.concat(additionalValidators);
  }

  createClientFormGroup(getSignFieldsByClientCid: SignFieldsByClientCidGetter): FormGroup {
    const profile = this.profileService.getCurrentProfile();

    const formGroup = this.formBuilder.group(
      {
        cid: [uuidv4(), [Validators.required]],
        forename: ['', [Validators.required]],
        surname: ['', [Validators.required]],
        birthYear: ['', [NumbersOnlyValidator]],
        personalId: ['', [Validators.required]],
        email: [''],
        phone: [''],
        signingRequest: this.formBuilder.group({
          channel: [SigningRequestChannel.None, [Validators.required]],
          subject: [profile.defaultRequestSignatureSubject],
          body: [profile.defaultRequestSignatureBody],
        }),
      },
      {
        validators: [VisibleClientFieldsValidator(profile.clientFieldsVisibility)],
      }
    );

    formGroup
      .get('email')
      .setValidators([
        RequestChannelContactValidator(
          () => formGroup.get('signingRequest').get('channel').value,
          SigningRequestChannel.Email
        ),
        SignFieldsContactValidator(
          getSignFieldsByClientCid,
          () => formGroup.get('cid').value,
          SignatureType.Email
        ),
        Validators.email,
      ]);

    formGroup
      .get('phone')
      .setValidators([
        RequestChannelContactValidator(
          () => formGroup.get('signingRequest').get('channel').value,
          SigningRequestChannel.SMS
        ),
        SignFieldsContactValidator(
          getSignFieldsByClientCid,
          () => formGroup.get('cid').value,
          SignatureType.SMS
        ),
      ]);

    formGroup
      .get('signingRequest')
      .get('channel')
      .valueChanges.subscribe(() => {
        formGroup.get('email').updateValueAndValidity();
        formGroup.get('phone').updateValueAndValidity();
      });

    return formGroup;
  }

  createClientsToDocsFormArray(clientsToDocs?: ITransactionFormDataDocRelations[]): FormArray {
    if (Array.isArray(clientsToDocs)) {
      return this.formBuilder.array(
        clientsToDocs.map((docRelations) => this.createDocRelationsFormArray(docRelations))
      );
    }
    return this.formBuilder.array([]);
  }

  createDocRelationsFormArray(docRelations?: ClientDocumentRelation[]): FormArray {
    if (Array.isArray(docRelations)) {
      return this.formBuilder.array(
        docRelations.map((relation) => this.createDocRelationFormControl()),
        [DocumentRelationValidator]
      );
    }
    return this.formBuilder.array([]);
  }

  createDocRelationFormControl(): FormControl {
    return this.formBuilder.control(null, Validators.required);
  }
}
